'use client';

import * as React from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { parseDate, formatDateISO, formatDateDisplay } from '@/utils/date';

type DatePickerProps<T extends FieldValues = FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label?: string;
  description?: string;
  required?: boolean;
};

export function DatePicker<T extends FieldValues = FieldValues>({
  control,
  name,
  label,
  description,
  required,
}: DatePickerProps<T>) {
  const { field } = useController({
    control,
    name: name as Path<T>,
  });

  const [open, setOpen] = React.useState(false);

  const date = React.useMemo(() => parseDate(field.value), [field.value]);
  const [month, setMonth] = React.useState<Date>(() => {
    const initialDate = parseDate(field.value);
    return initialDate || new Date();
  });

  React.useEffect(() => {
    const parsedDate = parseDate(field.value);
    if (parsedDate) {
      setMonth(parsedDate);
    }
  }, [field.value]);

  const handleCalendarSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      field.onChange(formatDateISO(selectedDate));
      setOpen(false);
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={name}>
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" id={name} className="w-full justify-between font-normal">
            {date ? formatDateDisplay(date) : 'Select date'}
            <ChevronDownIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            month={month}
            onMonthChange={setMonth}
            onSelect={handleCalendarSelect}
            captionLayout="dropdown"
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
